
import React, { useState, useEffect } from 'react';

const AnimatedNumber = ({ value, duration = 1000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let startTime;
        let animationFrame;
        const targetValue = Math.round(Number(value) || 0);

        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);

            // Ease out cubic function for smooth deceleration
            const ease = 1 - Math.pow(1 - progress, 3);

            setCount(Math.floor(ease * targetValue));

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrame);
    }, [value, duration]);

    return <>{count}</>;
};

const ProgressOverview = ({ stats }) => {
    const [progressWidth, setProgressWidth] = useState(0);

    useEffect(() => {
        // Trigger animation after mount
        const timer = setTimeout(() => {
            setProgressWidth(Math.round(Number(stats.completion) || 0));
        }, 100);
        return () => clearTimeout(timer);
    }, [stats.completion]);

    return (
        <div className="flex flex-col h-full">
            {/* Header Tabs */}
            <div className="flex mb-6">
                <div
                    className="bg-[#D4B95E] text-white px-10 py-3 font-medium text-base relative z-10 pr-16 shadow-md"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                >
                    Progress Overview
                </div>
                <div
                    className="bg-[#1E4C56] text-white px-10 py-3 font-medium text-base -ml-10 pl-16 relative z-0 pr-16 shadow-md"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                >
                    Minor Delays
                </div>
            </div>

            <h2 className="text-xl font-normal text-gray-900 mb-6">This week at a glance,</h2>

            <div className="flex flex-col">
                {/* Top Split Section - 3 Cards */}
                <div className="flex gap-4 md:gap-8 items-stretch relative z-10">
                    {/* Planned Card */}
                    <div className="bg-white rounded-tl-2xl rounded-tr-2xl p-6 pb-6 flex flex-col flex-1 min-h-[160px] shadow-md">
                        <span className="text-xl font-bold text-black leading-none mb-4">Planned</span>
                        <span className="text-[64px] font-bold text-black leading-none">
                            <AnimatedNumber value={stats.planned} />
                        </span>
                    </div>

                    {/* Done Card */}
                    <div className="bg-white rounded-tl-2xl rounded-tr-2xl p-6 pb-6 flex flex-col flex-1 min-h-[160px] shadow-md">
                        <span className="text-xl font-bold text-black leading-none mb-4">Done</span>
                        <span className="text-[64px] font-bold text-black leading-none">
                            <AnimatedNumber value={stats.done} />
                        </span>
                    </div>

                    {/* Delayed Card */}
                    <div className="bg-white rounded-tl-2xl rounded-tr-2xl p-6 pb-6 flex flex-col flex-1 min-h-[160px] shadow-md">
                        <span className="text-xl font-bold text-black leading-none mb-4">Delayed</span>
                        <span className="text-[64px] font-bold text-black leading-none">
                            <AnimatedNumber value={stats.delayed} />
                        </span>
                    </div>
                </div>

                {/* Unified Bottom Section - Weekly Completion */}
                <div className="bg-white rounded-b-2xl p-6 pt-4 -mt-4 relative z-20 shadow-md">
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-end mb-1">
                            <span className="text-base text-gray-600">Weekly Completion</span>
                            <span className="text-xl font-bold text-black leading-none">
                                <AnimatedNumber value={stats.completion} />%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 relative">
                            <div
                                className="bg-[#1F4B55] h-3 rounded-full relative transition-all duration-1000 ease-out"
                                style={{ width: `${progressWidth}%` }}
                            >
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProgressOverview;
